class CollectionNavDropdown extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.items = [];
    this.mql = window.matchMedia('(min-width: 750px)');
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
  }

  connectedCallback() {
    const dataEl = document.querySelector('[data-collection-nav-v2]');
    if (!dataEl) return;

    try {
      this.items = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    if (!this.items.length) return;

    this.render();
    this.bindEvents();
    this.mql.addEventListener('change', this.onMediaChange);
    this.onMediaChange();
  }

  disconnectedCallback() {
    this.mql.removeEventListener('change', this.onMediaChange);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  get activeItem() {
    return this.items.find((item) => item.active) || this.items[0];
  }

  render() {
    const active = this.activeItem;
    const listId = `CollectionNavList-${this.dataset.sectionId || 'default'}`;

    this.innerHTML = `
      <div class="collection-grid-v2__dropdown">
        <button
          type="button"
          class="collection-grid-v2__dropdown-trigger"
          aria-expanded="false"
          aria-haspopup="listbox"
          aria-controls="${listId}"
        >
          <span class="collection-grid-v2__dropdown-label">${this.escapeHtml(active.title)}</span>
          <span class="collection-grid-v2__dropdown-icon" aria-hidden="true"></span>
        </button>
        <div class="collection-grid-v2__dropdown-panel" hidden>
          <ul id="${listId}" class="collection-grid-v2__dropdown-list" role="listbox" aria-label="Collection navigation">
            ${this.items
              .map((item, index) => {
                const isActive = item.active;
                const optionClass = isActive
                  ? 'collection-grid-v2__dropdown-option collection-grid-v2__dropdown-option--active'
                  : 'collection-grid-v2__dropdown-option';

                if (item.url) {
                  return `
                    <li class="collection-grid-v2__dropdown-item" role="presentation">
                      <a
                        href="${this.escapeAttr(item.url)}"
                        class="${optionClass}"
                        role="option"
                        ${isActive ? 'aria-selected="true"' : 'aria-selected="false"'}
                        tabindex="${isActive ? '0' : '-1'}"
                        data-index="${index}"
                      >${this.escapeHtml(item.title)}</a>
                    </li>
                  `;
                }

                return `
                  <li class="collection-grid-v2__dropdown-item" role="presentation">
                    <span
                      class="${optionClass}"
                      role="option"
                      aria-selected="${isActive}"
                      aria-disabled="true"
                    >${this.escapeHtml(item.title)}</span>
                  </li>
                `;
              })
              .join('')}
          </ul>
        </div>
      </div>
    `;

    this.trigger = this.querySelector('.collection-grid-v2__dropdown-trigger');
    this.panel = this.querySelector('.collection-grid-v2__dropdown-panel');
    this.label = this.querySelector('.collection-grid-v2__dropdown-label');
    this.options = Array.from(this.querySelectorAll('.collection-grid-v2__dropdown-option[href]'));
    this.injectCaretIcon();
  }

  injectCaretIcon() {
    const iconTarget = this.querySelector('.collection-grid-v2__dropdown-icon');
    if (!iconTarget || iconTarget.childElementCount > 0) return;

    const sortIcon = document.querySelector('.collection-grid-v2__sort-icon');
    if (sortIcon) {
      iconTarget.innerHTML = sortIcon.innerHTML;
    }
  }

  bindEvents() {
    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    });

    this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
    this.panel.addEventListener('keydown', (event) => this.onPanelKeydown(event));

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.close());
    });
  }

  onMediaChange() {
    if (this.mql.matches) {
      this.close();
      this.hidden = true;
    } else {
      this.hidden = false;
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.removeAttribute('hidden');
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);

    const selected = this.panel.querySelector('[aria-selected="true"]');
    if (selected && selected.focus) {
      selected.focus();
    }
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('hidden', '');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.trigger.focus();
    }
  }

  onTriggerKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.open();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.open();
        if (this.options.length) {
          this.options[this.options.length - 1].focus();
        }
        break;
    }
  }

  onPanelKeydown(event) {
    const focusable = this.options;
    const currentIndex = focusable.indexOf(document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusable[(currentIndex + 1) % focusable.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusable[(currentIndex - 1 + focusable.length) % focusable.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        focusable[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        focusable[focusable.length - 1]?.focus();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(text) {
    return String(text).replace(/"/g, '&quot;');
  }
}

if (!customElements.get('collection-nav-dropdown')) {
  customElements.define('collection-nav-dropdown', CollectionNavDropdown);
}
